import { useState, type FormEvent } from "react";
import {
  AlertCircle,
  CheckCircle2,
  FileArchive,
  Layers,
  Loader2,
  Send,
  UploadCloud,
  X,
} from "lucide-react";
import type { AppSummary, PublishReleaseRequest, PublishReleaseResponse } from "../contract.js";
import { apiErrorMessage } from "../lib/api-error.js";
import { formatBytes } from "../lib/format.js";
import { useActivateReleaseMutation, usePublishReleaseMutation } from "../store/api.js";
import { CompatibilityVerdict } from "./CompatibilityPanel.js";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

interface PublishDialogProps {
  apps: AppSummary[];
  defaultChannel: string;
  onClose: () => void;
}

type UploadStage = "menunggu" | "mengunggah" | "selesai" | "gagal";

interface UploadItem {
  key: string;
  file: File;
  stage: UploadStage;
  uploaded?: PublishReleaseResponse;
  uploadError?: string;
  activated?: boolean;
  activateError?: string;
}

export function PublishDialog({ apps, defaultChannel, onClose }: PublishDialogProps) {
  const [publishRelease] = usePublishReleaseMutation();
  const [activateRelease] = useActivateReleaseMutation();

  const channelNames = [...new Set(apps.flatMap((app) => app.channels.map((one) => one.name)))];

  const [channel, setChannel] = useState(defaultChannel || channelNames[0] || "production");
  const [releaseNotes, setReleaseNotes] = useState("");
  const [rolloutPercent, setRolloutPercent] = useState(10);
  const [mandatory, setMandatory] = useState(false);
  const [files, setFiles] = useState<File[]>([]);
  const [localError, setLocalError] = useState("");
  const [items, setItems] = useState<UploadItem[]>([]);
  const [activating, setActivating] = useState(false);

  function patchItem(key: string, patch: Partial<UploadItem>) {
    setItems((current) => current.map((one) => (one.key === key ? { ...one, ...patch } : one)));
  }

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    if (files.length === 0) {
      setLocalError("Pilih file zip bundle OTA dari folder dist/ aplikasi.");
      return;
    }
    if (!channel) {
      setLocalError("Pilih channel tujuan bundle ini.");
      return;
    }
    setLocalError("");

    const meta: PublishReleaseRequest = {
      channel,
      releaseNotes: releaseNotes.trim() || undefined,
      mandatory,
      rolloutPercent,
      activate: false,
    };

    const queued: UploadItem[] = files.map((file, index) => ({
      key: `${index}:${file.name}:${file.size}:${file.lastModified}`,
      file,
      stage: "menunggu",
    }));
    setItems(queued);

    for (const item of queued) {
      patchItem(item.key, { stage: "mengunggah" });
      const result = await publishRelease({ bundle: item.file, meta });
      if ("error" in result) {
        patchItem(item.key, { stage: "gagal", uploadError: apiErrorMessage(result.error) });
      } else {
        patchItem(item.key, { stage: "selesai", uploaded: result.data });
      }
    }
  }

  async function activateAll() {
    setActivating(true);
    for (const item of items) {
      const draft = item.uploaded;
      if (!draft || item.activated) continue;
      const result = await activateRelease({ id: draft.release.id });
      if ("error" in result) {
        patchItem(item.key, { activateError: apiErrorMessage(result.error) });
      } else {
        patchItem(item.key, { activated: true, activateError: undefined });
      }
    }
    setActivating(false);
  }

  const uploading = items.some((one) => one.stage === "menunggu" || one.stage === "mengunggah");
  const drafts = items.filter((one) => one.uploaded);
  const pendingDrafts = drafts.filter((one) => !one.activated);

  if (items.length > 0) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-xs p-4">
        <div className="w-full max-w-2xl rounded-2xl bg-white p-6 shadow-2xl border border-[#EAECF0] max-h-[90vh] flex flex-col">
          <div className="flex items-center justify-between pb-4 border-b border-[#EAECF0]">
            <div>
              <h2 className="text-base font-bold text-[#101828]">
                {uploading ? "Mengunggah Bundle OTA…" : `Tinjau ${drafts.length} Draf Rilis`}
              </h2>
              <p className="text-xs text-[#667085]">
                Periksa kompatibilitas rilis sebelum didistribusikan ke perangkat.
              </p>
            </div>
            <button type="button" onClick={onClose} className="rounded p-1 text-[#98A2B3] hover:text-[#101828]">
              <X className="size-5" />
            </button>
          </div>

          <div className="mt-4 flex-1 overflow-y-auto space-y-4">
            {items.map((item) => (
              <div key={item.key} className="rounded-xl border border-[#EAECF0] bg-[#F6F7F9]/50 p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <FileArchive className="size-4 text-[#2563EB]" />
                    <span className="font-mono text-xs font-semibold text-[#101828]">{item.file.name}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span
                      className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium ${
                        item.stage === "selesai"
                          ? "bg-[#ECFDF3] text-[#027A48]"
                          : item.stage === "gagal"
                          ? "bg-[#FEF3F2] text-[#B42318]"
                          : "bg-[#EFF4FF] text-[#2563EB]"
                      }`}
                    >
                      {item.stage === "mengunggah" && <Loader2 className="size-3 animate-spin" />}
                      {item.stage}
                    </span>
                    {item.activated && (
                      <span className="rounded-full bg-[#ECFDF3] border border-[#ABEFC6] px-2 py-0.5 text-[11px] font-semibold text-[#027A48]">
                        Aktif
                      </span>
                    )}
                  </div>
                </div>

                {item.uploaded && (
                  <div className="text-xs space-y-2 pt-2 border-t border-[#EAECF0]">
                    <div className="grid grid-cols-2 gap-2 text-[#667085]">
                      <div>
                        Tujuan:{" "}
                        <span className="font-medium text-[#101828]">
                          {item.uploaded.release.channel} · {item.uploaded.release.platform}
                        </span>
                      </div>
                      <div>
                        Versi Native:{" "}
                        <span className="font-mono font-medium text-[#101828]">
                          v{item.uploaded.release.nativeVersion}
                        </span>
                      </div>
                      <div>
                        Ukuran:{" "}
                        <span className="font-mono text-[#101828]">
                          {formatBytes(item.uploaded.release.sizeBytes)}
                        </span>
                      </div>
                      <div>
                        Rollout:{" "}
                        <span className="font-mono font-medium text-[#101828]">
                          {item.uploaded.release.rolloutPercent}%
                        </span>
                      </div>
                    </div>
                    <CompatibilityVerdict compatibility={item.uploaded.compatibility} />
                  </div>
                )}

                {item.uploadError && (
                  <div className="flex items-center gap-1.5 text-xs text-[#B42318] pt-1">
                    <AlertCircle className="size-3.5 shrink-0" />
                    <span>{item.uploadError}</span>
                  </div>
                )}
                {item.activateError && (
                  <div className="flex items-center gap-1.5 text-xs text-[#B42318] pt-1">
                    <AlertCircle className="size-3.5 shrink-0" />
                    <span>Aktivasi gagal: {item.activateError}</span>
                  </div>
                )}
              </div>
            ))}
          </div>

          <div className="mt-4 pt-4 border-t border-[#EAECF0] flex items-center justify-between">
            <Button variant="ghost" size="sm" onClick={onClose} className="text-xs text-[#667085]">
              {drafts.length > 0 && pendingDrafts.length === 0 ? "Tutup" : "Biarkan Sebagai Draf"}
            </Button>
            <Button
              size="sm"
              onClick={activateAll}
              disabled={uploading || activating || pendingDrafts.length === 0}
              className="bg-[#0F1B3D] text-white hover:bg-[#1D2939] text-xs gap-1.5"
            >
              {activating ? (
                <>
                  <Loader2 className="size-3.5 animate-spin" />
                  Mengaktifkan…
                </>
              ) : (
                <>
                  <Send className="size-3.5" />
                  {pendingDrafts.length === 0 ? "Semua Sudah Aktif" : "Aktifkan Rilis"}
                </>
              )}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-xs p-4">
      <div className="w-full max-w-xl rounded-2xl bg-white p-6 shadow-2xl border border-[#EAECF0]">
        <div className="flex items-center justify-between pb-3 border-b border-[#EAECF0]">
          <div>
            <h2 className="text-base font-bold text-[#101828]">Buat Rilis OTA Baru</h2>
            <p className="text-xs text-[#667085]">Unggah berkas bundle zip untuk didistribusikan ke aplikasi.</p>
          </div>
          <button type="button" onClick={onClose} className="rounded p-1 text-[#98A2B3] hover:text-[#101828]">
            <X className="size-5" />
          </button>
        </div>

        <form onSubmit={onSubmit} className="mt-4 space-y-4">
          {/* File Picker Zone */}
          <div className="rounded-xl border-2 border-dashed border-[#EAECF0] p-5 text-center hover:border-[#2563EB] transition-colors">
            <UploadCloud className="mx-auto size-8 text-[#98A2B3]" />
            <p className="mt-2 text-xs font-medium text-[#101828]">
              Pilih satu atau dua berkas bundle OTA (.zip)
            </p>
            <p className="mt-1 text-[11px] text-[#667085]">
              Dihasilkan oleh <code>npm run ota:bundle</code> di folder <code>dist/</code>
            </p>
            <input
              type="file"
              accept=".zip"
              multiple
              onChange={(e) => setFiles(Array.from(e.target.files ?? []))}
              className="mt-3 block w-full text-xs text-[#667085] file:mr-2 file:py-1 file:px-3 file:rounded-md file:border-0 file:text-xs file:font-semibold file:bg-[#EFF4FF] file:text-[#2563EB] hover:file:bg-[#DBEAFE] cursor-pointer"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs text-[#344054]">Kanal Distribusi</Label>
              <select
                value={channel}
                onChange={(e) => setChannel(e.target.value)}
                className="w-full h-9 rounded-lg border border-[#D0D5DD] bg-white px-2.5 text-xs text-[#101828] focus:outline-none"
              >
                {channelNames.map((ch) => (
                  <option key={ch} value={ch}>
                    {ch}
                  </option>
                ))}
              </select>
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs text-[#344054]">Persentase Rollout Awal (%)</Label>
              <Input
                type="number"
                min={1}
                max={100}
                value={rolloutPercent}
                onChange={(e) => setRolloutPercent(Number(e.target.value))}
                className="h-9 text-xs border-[#D0D5DD]"
              />
            </div>
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs text-[#344054]">Catatan Rilis (Changelog)</Label>
            <textarea
              value={releaseNotes}
              onChange={(e) => setReleaseNotes(e.target.value)}
              placeholder="Jelaskan perubahan atau perbaikan pada rilis ini…"
              rows={3}
              className="w-full rounded-lg border border-[#D0D5DD] p-2.5 text-xs text-[#101828] focus:outline-none focus:border-[#2563EB]"
            />
          </div>

          <div className="flex items-center gap-2 text-xs text-[#344054]">
            <input
              type="checkbox"
              id="isMandatory"
              checked={mandatory}
              onChange={(e) => setMandatory(e.target.checked)}
              className="rounded border-[#D0D5DD] text-[#2563EB]"
            />
            <label htmlFor="isMandatory" className="cursor-pointer">
              Rilis Wajib (Perangkat harus memperbarui sebelum menggunakan aplikasi)
            </label>
          </div>

          {localError && (
            <p className="text-xs text-[#F04438]">{localError}</p>
          )}

          <div className="pt-3 border-t border-[#EAECF0] flex justify-end gap-2.5">
            <Button variant="outline" size="sm" type="button" onClick={onClose} className="text-xs">
              Batal
            </Button>
            <Button
              size="sm"
              type="submit"
              className="bg-[#0F1B3D] text-white hover:bg-[#1D2939] text-xs"
            >
              Lanjutkan Unggah ({files.length} berkas)
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
